import { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';

import { useAppDispatch, useAppSelector } from '@core/store/hooks';
import { Filename, MarkdownViewer, Spinner } from '@core/components/ui';
import config from '@core/config';
import { sessionFetch } from '@core/api/sessionFetch';
import { selectIsGuest } from '@features/auth';
import { selectCurrentProject } from '@features/projects';
import {
  useLazyGetPreviewUrlQuery,
  useLazyGetDownloadUrlQuery,
  useLazyGetWopiTokenQuery,
  useGetDocumentVersionsQuery,
  useLazyGetVersionPreviewUrlQuery,
  useLazyGetVersionDiffQuery,
  useRestoreVersionMutation,
  dataroomApi,
  CollaboraViewer,
  CsvViewer,
  DiffViewer,
  ImageViewer,
  TextViewer,
  VersionHistoryPanel,
} from '@features/dataroom';
import { useLazyGetAgentFileWopiTokenQuery } from '@features/ai';

import { selectCanvasDocument, selectCanvasOpen, selectCanvasWidth, selectCanvasRefreshToken, setCanvasWidth, closeCanvas } from '../store/workspaceSlice';
import { CanvasPdfViewer } from './CanvasPdfViewer';

const OFFICE_TYPES = new Set(['.docx', '.doc', '.pptx', '.ppt', '.xlsx', '.xls']);
const IMAGE_TYPES = new Set(['.png', '.jpg', '.jpeg']);

const COLLABORA_URL = import.meta.env.VITE_COLLABORA_URL || `${window.location.protocol}//${window.location.hostname}:9980`;

const FILE_ICONS = {
  '.pdf': 'mdi:file-pdf-box',
  '.docx': 'mdi:file-word-box',
  '.doc': 'mdi:file-word-box',
  '.pptx': 'mdi:file-powerpoint-box',
  '.ppt': 'mdi:file-powerpoint-box',
  '.xlsx': 'mdi:file-excel-box',
  '.xls': 'mdi:file-excel-box',
  '.png': 'mdi:file-image-box',
  '.jpg': 'mdi:file-image-box',
  '.jpeg': 'mdi:file-image-box',
  '.txt': 'mdi:file-document-outline',
  '.md': 'mdi:language-markdown',
  '.csv': 'mdi:file-delimited-outline',
  '.zip': 'mdi:folder-zip',
};

function MarkdownEditor({ url, readOnly = false }) {
  const { t } = useTranslation();
  const [content, setContent] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setIsLoading(true);

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(t('workspace.canvas.fetchFailed', { status: res.status }));
        return res.text();
      })
      .then((text) => { if (!cancelled) { setContent(text); setEditContent(text); } })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setIsLoading(false); });

    return () => { cancelled = true; };
  }, [url, t]);

  if (isLoading) return <div className="flex items-center justify-center p-12"><Spinner size="lg" /></div>;
  if (error) return <div className="p-4 text-sm text-red-600">{error}</div>;

  // Guests get the rendered view only — no Preview/Edit toggle at all.
  if (readOnly) {
    return (
      <div className="h-full overflow-auto">
        <div className="p-6">
          <MarkdownViewer content={content} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-[var(--color-border)] shrink-0">
        <button
          type="button"
          onClick={() => setIsEditing(false)}
          className={`cursor-pointer text-xs px-2 py-1 rounded ${!isEditing ? 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)] font-medium' : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-muted)]'}`}
        >
          {t('workspace.canvas.preview')}
        </button>
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className={`cursor-pointer text-xs px-2 py-1 rounded ${isEditing ? 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)] font-medium' : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-muted)]'}`}
        >
          {t('workspace.canvas.edit')}
        </button>
      </div>
      <div className="flex-1 overflow-auto">
        {isEditing ? (
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="w-full h-full p-4 text-sm font-mono resize-none focus:outline-none bg-gray-50"
          />
        ) : (
          <div className="p-6">
            <MarkdownViewer content={content} />
          </div>
        )}
      </div>
    </div>
  );
}

function AgentFileContent({ document: doc }) {
  const { t } = useTranslation();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [wopiData, setWopiData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [triggerAgentWopiToken] = useLazyGetAgentFileWopiTokenQuery();

  useEffect(() => {
    if (!doc?.url) return;
    setIsLoading(true);
    setWopiData(null);
    setPreviewUrl(null);
    setLoadError(null);

    if (OFFICE_TYPES.has(doc.file_type)) {
      // Extract org_uuid from the proxy URL: /api/ai/files/{org_uuid}/sandbox_outputs/{filename}
      const match = doc.url.match(/\/api\/ai\/files\/([^/]+)\/sandbox_outputs\/(.+)$/);
      if (match) {
        triggerAgentWopiToken({ orgUuid: match[1], filename: decodeURIComponent(match[2]) })
          .then(({ data }) => {
            if (!data) throw new Error(t('workspace.canvas.editingSessionFailed'));
            setWopiData(data);
            setIsLoading(false);
          })
          .catch((error) => {
            setLoadError(error.message || t('workspace.canvas.editingSessionFailed'));
            setIsLoading(false);
          });
        return;
      }
    }

    // For non-Office files, ask the proxy for the presigned URL as JSON and
    // hand it to the viewer, which loads it without credentials, exactly like
    // a dataroom document. The proxy is authenticated on another origin, so
    // the request needs the session cookie; following its redirect into the
    // storage origin with credentials fails the CORS preflight and Safari
    // reports a bare "Load failed". Never fall back to the proxy URL on
    // failure: the PDF viewer would just report the status code to the user.
    sessionFetch(`${doc.url}${doc.url.includes('?') ? '&' : '?'}json=1`)
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(t('workspace.canvas.fileLoadFailedStatus', { status: res.status }));
        }
        const { url } = await res.json();
        if (!url) throw new Error(t('workspace.canvas.fileLoadFailed'));
        setPreviewUrl(url);
        setIsLoading(false);
      })
      .catch((error) => {
        setLoadError(error.message || t('workspace.canvas.fileLoadFailed'));
        setIsLoading(false);
      });
  }, [doc?.url, doc?.file_type, triggerAgentWopiToken, t]);

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 p-12 text-center">
        <Icon icon="mdi:alert-circle-outline" className="size-8 text-[var(--color-danger)]" />
        <p className="text-sm text-[var(--color-text)]">{loadError}</p>
      </div>
    );
  }

  if (isLoading) {
    return <div className="flex items-center justify-center p-12"><Spinner size="lg" /></div>;
  }

  const fileType = doc.file_type;

  // Office files via Collabora
  if (OFFICE_TYPES.has(fileType) && wopiData) {
    return (
      <CollaboraViewer
        wopiSrc={wopiData.wopi_src}
        accessToken={wopiData.access_token}
        collaboraUrl={COLLABORA_URL}
      />
    );
  }

  if (!previewUrl) {
    return <div className="flex items-center justify-center p-12"><Spinner size="lg" /></div>;
  }

  if (fileType === '.pdf') return <CanvasPdfViewer url={previewUrl} />;
  if (fileType === '.csv') return <CsvViewer url={previewUrl} />;
  if (fileType === '.txt') return <TextViewer url={previewUrl} />;
  if (fileType === '.md') return <MarkdownEditor url={previewUrl} />;
  if (IMAGE_TYPES.has(fileType)) return <ImageViewer url={previewUrl} />;

  return (
    <div className="flex flex-col items-center justify-center p-8 text-[var(--color-text-secondary)]">
      <Icon icon="mdi:file-question" className="size-12 mb-2" />
      <p className="text-sm">{t('workspace.canvas.previewUnavailable', { fileType })}</p>
    </div>
  );
}

function CanvasContent({ document: doc, projectUuid, readOnly = false }) {
  const { t } = useTranslation();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [wopiData, setWopiData] = useState(null);
  const refreshToken = useAppSelector(selectCanvasRefreshToken);

  const [triggerPreviewUrl] = useLazyGetPreviewUrlQuery();
  const [triggerWopiToken] = useLazyGetWopiTokenQuery();

  useEffect(() => {
    if (!doc || doc.isAgentFile) return;
    setPreviewUrl(null);
    setWopiData(null);

    const params = { projectUuid, documentUuid: doc.uuid };

    if (OFFICE_TYPES.has(doc.file_type)) {
      // Canvas = edit mode for Office files; guests always get a view-only token
      triggerWopiToken({ ...params, editable: !readOnly }).then(({ data }) => {
        if (data) setWopiData(data);
      });
    } else {
      // A fresh presigned URL each time (incl. after an agent edit bumps
      // refreshToken) remounts the viewer with the re-rendered file.
      triggerPreviewUrl(params).then(({ data }) => {
        if (data?.url) setPreviewUrl(data.url);
      });
    }
  }, [doc, projectUuid, readOnly, triggerPreviewUrl, triggerWopiToken, refreshToken]);

  // Agent-generated files use their own viewer
  if (doc.isAgentFile) {
    return <AgentFileContent document={doc} />;
  }

  const fileType = doc.file_type;

  // Office files in edit mode
  if (OFFICE_TYPES.has(fileType)) {
    if (!wopiData) return <div className="flex items-center justify-center p-12"><Spinner size="lg" /></div>;
    return (
      <CollaboraViewer
        wopiSrc={wopiData.wopi_src}
        accessToken={wopiData.access_token}
        collaboraUrl={COLLABORA_URL}
        reviewMode={Boolean(wopiData.is_suggestion_review)}
      />
    );
  }

  // Markdown with edit tab
  if (fileType === '.md' && previewUrl) {
    return <MarkdownEditor url={previewUrl} readOnly={readOnly} />;
  }

  if (!previewUrl) {
    return <div className="flex items-center justify-center p-12"><Spinner size="lg" /></div>;
  }

  // Read-only viewers
  if (fileType === '.pdf') return <CanvasPdfViewer url={previewUrl} />;
  if (fileType === '.csv') return <CsvViewer url={previewUrl} />;
  if (fileType === '.txt') return <TextViewer url={previewUrl} />;
  if (IMAGE_TYPES.has(fileType)) return <ImageViewer url={previewUrl} />;

  return (
    <div className="flex flex-col items-center justify-center p-8 text-[var(--color-text-secondary)]">
      <Icon icon="mdi:file-question" className="size-12 mb-2" />
      <p className="text-sm">{t('workspace.canvas.previewUnavailable', { fileType })}</p>
    </div>
  );
}

const CANVAS_MIN_WIDTH = 320;
const CANVAS_MAX_WIDTH = 960;

export function CanvasPanel() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(selectCanvasOpen);
  const canvasDocument = useAppSelector(selectCanvasDocument);
  const canvasWidth = useAppSelector(selectCanvasWidth);
  const currentProject = useAppSelector(selectCurrentProject);
  const isGuest = useAppSelector(selectIsGuest);
  const projectUuid = currentProject?.uuid;
  const canvasProjectUuid = canvasDocument?.project_uuid ?? projectUuid ?? null;
  const csrfToken = useAppSelector((state) => state.auth?.csrfToken);

  const [triggerDownloadUrl] = useLazyGetDownloadUrlQuery();
  const [triggerVersionPreviewUrl] = useLazyGetVersionPreviewUrlQuery();
  const [triggerVersionDiff] = useLazyGetVersionDiffQuery();
  const [restoreVersion] = useRestoreVersionMutation();
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
  const [isResizing, setIsResizing] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [diffData, setDiffData] = useState(null);
  const [activeVersionUuid, setActiveVersionUuid] = useState(null);
  const panelRef = useRef(null);

  const { data: versionsData } = useGetDocumentVersionsQuery(
    { projectUuid: canvasProjectUuid, documentUuid: canvasDocument?.uuid },
    { skip: !showVersions || !canvasDocument?.uuid || canvasDocument?.isAgentFile },
  );

  // Auto-select current version when panel opens
  useEffect(() => {
    if (versionsData?.length && !activeVersionUuid) {
      setActiveVersionUuid(versionsData[0].version_id);
    }
  }, [versionsData, activeVersionUuid]);

  const handleVersionPreview = useCallback(async (version) => {
    const { data } = await triggerVersionPreviewUrl({
      projectUuid: canvasProjectUuid,
      documentUuid: canvasDocument.uuid,
      versionId: version.version_id,
    });
    if (data?.url) {
      setDiffData(null);
      setActiveVersionUuid(version.version_id);
    }
  }, [canvasDocument, canvasProjectUuid, triggerVersionPreviewUrl]);

  const handleVersionDiff = useCallback(async (version) => {
    const { data } = await triggerVersionDiff({
      projectUuid: canvasProjectUuid,
      documentUuid: canvasDocument.uuid,
      versionId: version.version_id,
    });
    if (data) {
      setDiffData(data);
    }
  }, [canvasDocument, canvasProjectUuid, triggerVersionDiff]);

  const handleVersionDownload = useCallback(async (version) => {
    const { data } = await triggerVersionPreviewUrl({
      projectUuid: canvasProjectUuid,
      documentUuid: canvasDocument.uuid,
      versionId: version.version_id,
    });
    if (data?.url) {
      window.open(data.url, '_blank');
    }
  }, [canvasDocument, canvasProjectUuid, triggerVersionPreviewUrl]);

  const handleVersionRestore = useCallback(async (version) => {
    await restoreVersion({
      projectUuid: canvasProjectUuid,
      documentUuid: canvasDocument.uuid,
      versionId: version.version_id,
    });
  }, [canvasDocument, canvasProjectUuid, restoreVersion]);

  const handleResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);

    const startX = e.clientX;
    const startWidth = canvasWidth;

    const handleMouseMove = (moveEvent) => {
      const newWidth = Math.min(CANVAS_MAX_WIDTH, Math.max(CANVAS_MIN_WIDTH, startWidth - (moveEvent.clientX - startX)));
      dispatch(setCanvasWidth(newWidth));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [canvasWidth, dispatch]);

  const isAgentFile = canvasDocument?.isAgentFile;

  const handleOpenFullscreen = useCallback(() => {
    if (isAgentFile && canvasDocument?.url) {
      window.open(canvasDocument.url, '_blank');
      return;
    }
    if (!canvasDocument) return;
    const puuid = canvasProjectUuid || 'general';
    const viewerUrl = `/viewer/${puuid}/${canvasDocument.uuid}?type=${encodeURIComponent(canvasDocument.file_type)}`;
    window.open(viewerUrl, '_blank');
  }, [canvasDocument, canvasProjectUuid, isAgentFile]);

  const handleDownload = useCallback(async () => {
    if (isAgentFile && canvasDocument?.url) {
      window.open(canvasDocument.url, '_blank');
      return;
    }
    if (!canvasDocument) return;
    const { data } = await triggerDownloadUrl({
      projectUuid: canvasProjectUuid,
      documentUuid: canvasDocument.uuid,
    });
    if (data?.url) window.open(data.url, '_blank');
  }, [canvasDocument, canvasProjectUuid, triggerDownloadUrl, isAgentFile]);

  const handleSaveToProject = useCallback(async () => {
    if (!canvasDocument?.url) return;
    setSaveStatus('saving');
    try {
      const apiPath = projectUuid
        ? `/api/projects/${projectUuid}/documents/from-agent/`
        : '/api/projects/general/documents/from-agent/';
      const response = await sessionFetch(
        `${config.apiBaseUrl}${apiPath}`,
        {
          method: 'POST',
          csrfToken,
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            source_url: canvasDocument.url,
            filename: canvasDocument.name,
          }),
        },
      );
      if (response.ok) {
        setSaveStatus('saved');
        dispatch(dataroomApi.util.invalidateTags(['Folder', 'Document']));
      } else {
        setSaveStatus('error');
      }
    } catch {
      setSaveStatus('error');
    }
  }, [canvasDocument, csrfToken, dispatch, projectUuid]);

  // Reset save status and version state when document changes
  useEffect(() => {
    setSaveStatus(null);
    setShowVersions(false);
    setDiffData(null);
  }, [canvasDocument]);

  if (!isOpen || !canvasDocument) return null;

  const fileIcon = FILE_ICONS[canvasDocument.file_type] || 'mdi:file-outline';

  return (
    <div
      ref={panelRef}
      style={{ width: canvasWidth }}
      className="relative flex flex-col border-l border-[var(--color-border)] bg-white shrink-0"
    >
      {/* Resize handle */}
      <div
        onMouseDown={handleResizeMouseDown}
        className={`absolute top-0 left-0 w-1 h-full cursor-col-resize z-10 transition-colors hover:bg-[var(--color-accent)] ${isResizing ? 'bg-[var(--color-accent)]' : ''}`}
      />
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--color-border)] shrink-0">
        <Icon icon={fileIcon} className="size-4 text-[var(--color-text-secondary)] shrink-0" />
        <Filename
          name={canvasDocument.name}
          className="text-sm font-medium text-[var(--color-text)] flex-1"
        />
        {isAgentFile && !isGuest && (
          <button
            type="button"
            onClick={handleSaveToProject}
            disabled={saveStatus === 'saving' || saveStatus === 'saved'}
            className="cursor-pointer inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200"
            title={t('workspace.canvas.saveToFiles')}
          >
            {saveStatus === 'saving' && <Spinner size="xs" />}
            {saveStatus === 'saved' && <Icon icon="mdi:check" className="size-3.5" />}
            {saveStatus === 'error' && <Icon icon="mdi:alert-circle" className="size-3.5 text-red-500" />}
            {!saveStatus && <Icon icon="mdi:content-save-outline" className="size-3.5" />}
            {saveStatus === 'saved'
              ? t('workspace.canvas.saved')
              : saveStatus === 'error'
                ? t('workspace.canvas.error')
                : t('workspace.canvas.saveToFilesIn', { project: currentProject?.name || t('workspace.canvas.generalProject') })}
          </button>
        )}
        {!isAgentFile && (
          <button
            type="button"
            onClick={() => setShowVersions(prev => !prev)}
            className="cursor-pointer p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] rounded transition-colors"
            aria-label={t('workspace.canvas.versionHistory')}
            title={t('workspace.canvas.versionHistory')}
          >
            <Icon icon="mdi:history" className="size-4" />
          </button>
        )}
        <button
          type="button"
          onClick={handleOpenFullscreen}
          className="cursor-pointer p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] rounded transition-colors"
          aria-label={t('workspace.canvas.openInNewTab')}
          title={t('workspace.canvas.openInNewTab')}
        >
          <Icon icon="mdi:open-in-new" className="size-4" />
        </button>
        <button
          type="button"
          onClick={handleDownload}
          className="cursor-pointer p-1 text-[var(--color-text-muted)] hover:text-teal-600 rounded transition-colors"
          aria-label={t('workspace.canvas.download')}
        >
          <Icon icon="mdi:download-outline" className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => dispatch(closeCanvas())}
          className="cursor-pointer p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] rounded transition-colors"
          aria-label={t('workspace.canvas.close')}
        >
          <Icon icon="mdi:close" className="size-4" />
        </button>
      </div>

      {/* Content */}
      <div className="flex flex-1 overflow-hidden">
        <div className={`flex-1 overflow-hidden ${showVersions ? 'border-r border-[var(--color-border)]' : ''}`}>
          {diffData ? (
            <DiffViewer diff={diffData} onBack={() => setDiffData(null)} />
          ) : (
            <CanvasContent document={canvasDocument} projectUuid={canvasProjectUuid} readOnly={isGuest} />
          )}
        </div>
        {showVersions && (
          <div className="w-72 overflow-y-auto border-l border-[var(--color-border)] shrink-0">
            <div className="p-3 border-b border-[var(--color-border)]">
              <h3 className="text-sm font-medium text-[var(--color-text)]">{t('workspace.canvas.versionHistoryTitle')}</h3>
            </div>
            <VersionHistoryPanel
              versions={versionsData || []}
              activeVersionUuid={activeVersionUuid}
              canRestore={!isGuest}
              onPreview={handleVersionPreview}
              onDiff={handleVersionDiff}
              onDownload={handleVersionDownload}
              onRestore={handleVersionRestore}
            />
          </div>
        )}
      </div>
    </div>
  );
}
